import { ref, computed } from 'vue'
import { defineStore } from 'pinia'
import { CastroomId, createCastroomId, castroomIdEquals, VideoSourceType } from '@dronecast/shared'
import { dronecastApiService } from '@/services/dronecast.service'
import { storedAuth } from '@/services/auth.credentials'

export const config = {
  server: {
    host: import.meta.env.VITE_DRONECAST_HOST || '127.0.0.1',
    port: parseInt(import.meta.env.VITE_DRONECAST_PORT || '7720'),
    userEmail: import.meta.env.VITE_DRONECAST_USER_EMAIL || '',
    apiKey: import.meta.env.VITE_DRONECAST_API_KEY || '',
  },
};

export interface ICastingSource {
  castroomId: CastroomId
  droneName: string
  cameraName: string
}

export interface ICastroom {
  status: string
  castingSource: ICastingSource
  rtspUrl: string | null
  viewerCount: number
  mediaServerType?: string
  casterLinkStatus?: string
  sourceType?: VideoSourceType
  // 목록 UI용 썸네일 (SnapshotManager가 주기 캡쳐)
  thumbnailUrl?: string | null
  thumbnailUpdatedAt?: number
}

export const useDronecastStore = defineStore('dronecast', () => {
  const castRooms = ref<ICastroom[]>([])
  const currentCast = ref<ICastroom | null>(null)
  const loading = ref(false)
  const error = ref<string | null>(null)

  // 게시자 관련 상태
  const isPublishing = ref(false)
  const publishStreamId = ref<string | null>(null)

  // 시청자 관련 상태
  const isViewing = ref(false)
  const viewStreamId = ref<string | null>(null)
  const viewerId = ref<string | null>(null)

  // 계산된 속성
  const hasStreams = computed(() => castRooms.value.length > 0)
  const publisherDroneName = ref('')
  const publisherCameraName = ref('')

  // 브라우저 location 기반 WebSocket URL (로컬: Vite proxy, 배포: nginx proxy)
  const wsProto = location.protocol === 'https:' ? 'wss' : 'ws'
  // computed 다 — 로그인 전에 만들어지므로 ref 로 굳히면 빈 자격증명이 박힌다.
  const websocketUrl = computed<string>(() => {
    const { userEmail, apiKey } = storedAuth.value
    return `${wsProto}://${location.host}/ws?userEmail=${encodeURIComponent(userEmail)}&apiKey=${encodeURIComponent(apiKey)}`
  })

  async function fetchAllCastrooms() {
    return fetchDronecastList()
  }

  async function fetchDronecastList(gcsId?: string) {
    loading.value = true
    error.value = null

    try {
      const resultData = await dronecastApiService.fetchCastrooms(gcsId)

      castRooms.value = resultData.map((item) => ({
        status: item.status,
        castingSource: {
          castroomId: createCastroomId(item.castroomId.gcs, item.castroomId.drone, item.castroomId.camera),
          droneName: item.castroomId.drone,
          cameraName: item.castroomId.camera,
        },
        rtspUrl: item.rtspUrl ?? null,
        viewerCount: item.numberOfViewers,
        mediaServerType: (item as any).mediaServerType,
        casterLinkStatus: (item as any).casterLinkStatus,
        sourceType: (item as any).sourceType,
        thumbnailUrl: (item as any).thumbnailUrl ?? null,
        thumbnailUpdatedAt: (item as any).thumbnailUpdatedAt,
      } as ICastroom));

    } catch (err: any) {
      error.value = err.message || '스트림 목록을 불러오는데 실패했습니다.'
    } finally {
      loading.value = false
    }
  }

  async function fetchStream(streamId: string) {
    loading.value = true
    error.value = null

    try {
      const data = await dronecastApiService.fetchCastroom(streamId)
      currentCast.value = data
      return data
    } catch (err: any) {
      error.value = err.message || '스트림 정보를 불러오는데 실패했습니다.'
      return null
    } finally {
      loading.value = false
    }
  }

  function updateCastroomStatus(castroomId: CastroomId, status: string) {
    const castroom = castRooms.value.find(castroom => castroomIdEquals(castroom.castingSource.castroomId, castroomId))
    if (castroom) {
      castroom.status = status
    }
  }

  return {
    // 상태
    castroomList: castRooms,
    currentStream: currentCast,
    loading,
    error,
    isPublishing,
    publishStreamId,
    isViewing,
    viewStreamId,
    viewerId,
    publisherDroneName,
    publisherCameraName,
    websocketUrl,

    // 계산된 속성
    hasStreams,

    fetchDronecastList,
    fetchAllCastrooms,
    fetchStream,
    updateCastroomStatus,
  }
})
